import {
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import css from '@emotion/css';
import styled from '@emotion/styled';
import throttle from 'lodash/throttle';

const Container = styled.div<{
  scrolledTop: boolean;
  scrolledBottom: boolean;
}>`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-left: 25px;

  &::before,
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 8px;
    height: 50px;
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;
    z-index: 1;
  }
  &:before {
    top: 0;
    background: linear-gradient(
      to bottom,
      var(--color-background-white),
      transparent
    );
  }
  &:after {
    bottom: 0;
    background: linear-gradient(
      to top,
      var(--color-background-white),
      transparent
    );
  }
  ${({ scrolledTop, scrolledBottom }) => css`
    &::before {
      opacity: ${scrolledTop ? 0 : 1};
    }
    &::after {
      opacity: ${scrolledBottom ? 0 : 1};
    }
  `}
`;

export const UsageScrollContainer: FC<PropsWithChildren> = ({ children }) => {
  const [scrolledTop, setScrolledTop] = useState(false);
  const [scrolledBottom, setScrolledBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;

      const handleScroll = throttle(
        () => {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
          setScrolledTop(scrollTop === 0);
          setScrolledBottom(scrollTop + clientHeight >= scrollHeight - 1);
        },
        100,
        { leading: true, trailing: true }
      );

      const resizeObserver = new ResizeObserver(handleScroll);

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      resizeObserver.observe(scrollContainer);

      handleScroll();

      return () => {
        handleScroll.cancel();
        scrollContainer.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [scrollContainerRef]);

  return (
    <Container scrolledTop={scrolledTop} scrolledBottom={scrolledBottom}>
      <div ref={scrollContainerRef} className="kds-expanding-scrollbar">
        {children}
      </div>
    </Container>
  );
};
